import React, { useContext, useEffect, useState } from 'react'
import { Trans } from '@lingui/react'
import { observer } from 'mobx-react-lite'
import { useRouter } from 'next/router'
import {
  Container,
  Grid,
  GridCol,
  Icon,
  Notice,
  NoticeContentTypeEnum,
  NoticeList,
  NoticeTypeEnum,
  Section,
} from '../../common/ui-kit'

import { AcceptationPlaces } from '../../common/modules/dash-board/AcceptationPlaces'
import { ActivateCardTip } from '../../common/modules/dash-board/ActivateCardTip'
import { Banners } from '../../common/modules/dash-board/Banners'
import { CardsStore } from './CardsStore'
import { DashboardHead } from './DashboardHead'
import { EmployeeCtx } from '../EmployeeStore'
import { HeaderBanners } from '../../common/modules/dash-board/HeaderBanners'
import { News } from '../../common/modules/dash-board/News'
import { Sidebar } from './Sidebar'
import { StoreEndUserDashboard } from './StoreEndUserDashboard'
import { TransactionsTable } from './TransactionsTable'
import { TransactionsTableStore } from './TransactionsTableStore'
import { _ } from '../../common/utils/intl'
import { appStore } from '../../common/AppStore'

/* eslint-disable no-console */

export const PageDashboardCtx = React.createContext<{
  storeCards: CardsStore
  storeDashboard: StoreEndUserDashboard
  storeTransactions: TransactionsTableStore
}>(null as any)

export const PageDashboard = observer(() => {
  const employee = useContext(EmployeeCtx)
  const [storeCards] = useState(() => new CardsStore(employee))
  const [storeDashboard] = useState(() => new StoreEndUserDashboard())
  const [storeTransactions] = useState(() => new TransactionsTableStore())
  const router = useRouter()

  const cardIdRaw = router.query!.card_id

  const cardId = Array.isArray(cardIdRaw) ? cardIdRaw[0] : cardIdRaw

  useEffect(() => {
    storeCards.fetchEmpoyeeCards().then(() => {
      if (cardId && !storeCards.cards.find(card => card.id === cardId))
        throw new Error('Card doesnt exist')
      storeCards.setSelectedCardId(cardId)
    })
  }, [employee.id, storeCards, cardId])

  useEffect(() => {
    Promise.all([
      storeDashboard.fetchBanners(),
      storeDashboard.fetchArticles(),
      storeDashboard.fetchHints(),
    ]).catch()
  }, [storeDashboard])

  return (
    <PageDashboardCtx.Provider value={{ storeCards, storeDashboard, storeTransactions }}>
      <main id="main" className="enduser-dashboard">
        <Section color="light-grey" size="xxlarge">
          <Container>
            {storeCards.selectedCard && storeCards.selectedCard.can_activate_by_holder && (
              <ActivateCardTip cards={storeCards} />
            )}
            {appStore.currentUser?.dummy_email && !appStore.currentUser?.phone && (
              <Notice
                id="hint-fill-email"
                type="tip"
                renderIcon={() => <Icon name="bulb" size="xlarge" className="text-vam" />}
                closable
                closeSize="medium"
                className="text-color-turquoise-600 mb-xlarge">
                <div className="text-color-black">
                  <Trans>
                    Nastavte si e-mail alebo telefón, prostredníctvom ktorého sa môžete prihlasovať
                    a jednoduchšie obnoviť heslo v prípade zabudnutia.
                  </Trans>
                </div>
                <a
                  className="link-black text-bold text-color-turquoise-600"
                  style={{ border: 0 }}
                  href="/profile">
                  <Trans>Nastaviť e-mail alebo telefón</Trans>
                </a>
              </Notice>
            )}

            <NoticeList
              notices={storeDashboard.hints.map(hint => {
                return {
                  id: hint.id,
                  type: NoticeTypeEnum.hint,
                  contentType: hint.type === 'alert' ? NoticeContentTypeEnum.alert : undefined,
                  renderContent: (options: { textColor: string }) => {
                    return (
                      <>
                        <div
                          className="text-color-black"
                          dangerouslySetInnerHTML={{ __html: hint.text || '' }}
                        />
                        <a
                          className={`link-black text-bold ${options.textColor}`}
                          style={{ border: 0 }}
                          href={hint.link_url}
                          target={hint.link_new_window ? '_blank' : ''}
                          dangerouslySetInnerHTML={{ __html: hint.link_text || '' }}
                        />
                      </>
                    )
                  },
                }
              })}
            />

            <DashboardHead />

            <div className="mb-xlarge">
              <HeaderBanners banners={storeDashboard.banners} />
            </div>

            <Grid className="mb-xlarge">
              <GridCol size={{ m: 4, l: 3 }}>
                <Sidebar cards={storeCards} />
              </GridCol>
              <GridCol size={{ m: 8, l: 9 }}>
                <TransactionsTable />
                <AcceptationPlaces />
              </GridCol>
            </Grid>
            <Banners banners={storeDashboard.banners} />
            <News
              articles={storeDashboard.articles}
              articlesCategories={storeDashboard.articlesCategories}
            />
          </Container>
        </Section>
      </main>
    </PageDashboardCtx.Provider>
  )
})
